import { code } from 'currency-codes';
import Qty from 'js-quantities';
import { unit } from 'mathjs';
import type { IntlShape } from 'react-intl';
import UNITS from 'resources/json/Units.json';
import { type Currency } from 'types/models';
import { type EmissionWeightUnitEnum, EmissionWeightUnits, KPIFieldType, type KPIFieldTypeEnum } from 'utils/enum';

export const CANONICAL_EMISSIONS_API_UNIT = EmissionWeightUnits.kg;

export const convertCanonicalEmissionValue = (value: number, targetUnit: EmissionWeightUnitEnum) =>
   convertUnit(CANONICAL_EMISSIONS_API_UNIT, targetUnit, value);

const unitReplacement: Record<string, string | null> = {
   tonne: 't',
   'tonne-km': 't-km',
   'kg/mile': 'kg/mi',
   'kg/short ton': 'kg/tn',
   'kg/tonne': 'kg/t',
   'kg/room-night': 'kg',
   'kg/instance-hour': 'kg/h',
   'kg/CPU-hour': 'kg/h',
   'kg/passenger-km': 'kg/km',
   'kg/passenger-mile': 'kg/mile',
   'kg/person-night': 'kg',
   'kg/gal (US)': 'kg/gal',
   'kg/L': 'kg/l',
   'room-night': null,
   'CPU-hour': 'h',
   'instance-hour': 'h',
   'passenger-km': 'km',
   'passenger-mile': 'mile',
   'person-night': null,
   'gal (US)': 'gal',
   L: 'l',
   'container-moved': null,
   mile: 'mi',
   'employee-day': 'd',
   'kg/employment-day': 'kg/d',
   'kg/building': 'kg',
   bulding: null,
   'kg/person-year': 'kg/y',
   'person-year': 'y',
};

type Unit = string | null | undefined;

export function determineBaseUnit(unit: string): Unit {
   if (!unit) {
      return unit;
   }

   const [, baseUnit] = (unit ?? '').includes('/') ? unit.split('/') : [];

   if (baseUnit && code(baseUnit)) {
      return undefined;
   }

   if (baseUnit in unitReplacement) {
      const replacement = unitReplacement[baseUnit];
      if (replacement === null) {
         return null;
      }
      const finalUnit = baseUnit.replace(baseUnit, replacement);

      if (finalUnit === 'null') {
         return null;
      }

      return finalUnit;
   }
   return baseUnit;
}

function sanitizeUnit(unit: Unit): string {
   const replacementChars = ['/', '-'];
   let returnUnit = unit ?? '';

   switch (returnUnit) {
      case 'ha':
         returnUnit = 'hectare';
         break;
      case 'a':
         returnUnit = 'acre';
         break;
      case 'fl oz':
         returnUnit = 'floz';
         break;
      default:
   }

   returnUnit = returnUnit
      .replace(' CO₂', '')
      .replace('²', '^2')
      .replace('³', '^3')
      .replace(/\B(\d)/g, '^$1');

   replacementChars.forEach((char) => {
      if (returnUnit.includes(char)) {
         returnUnit = returnUnit
            .split(char)
            .map((aUnit) => {
               if (aUnit === 'ha') {
                  return 'hectare';
               }
               if (aUnit === 'a') {
                  return 'acre';
               }
               return aUnit;
            })
            .join(char);
      }
   });

   return returnUnit;
}

export function convertUnit(unitFrom: string, unitTo: string, amount: number) {
   const sourceUnit = sanitizeUnit(unitFrom);
   const targetUnit = sanitizeUnit(unitTo);

   if (sourceUnit === targetUnit || amount === 0) {
      return amount;
   }

   if ((sourceUnit.endsWith(' CO₂') && !targetUnit.endsWith(' CO₂')) || (!sourceUnit.endsWith(' CO₂') && targetUnit.endsWith(' CO₂'))) {
      throw new Error(JSON.stringify({ error: 'units_not_compatible', from: unitFrom ?? '', to: unitTo ?? '', amount }));
   }

   if ([sourceUnit, targetUnit].some((aUnit) => ['Gt', 'Mt'].includes(aUnit))) {
      return unit(`${amount}${sourceUnit}`).toNumber(targetUnit);
   }

   if ((!sourceUnit && targetUnit) || (targetUnit && !sourceUnit)) {
      throw new Error(JSON.stringify({ error: 'units_not_compatible', from: unitFrom ?? '', to: unitTo ?? '', amount }));
   }

   if (sourceUnit?.includes('-') && targetUnit?.includes('-')) {
      const [sourceBase, sourceModifier] = sourceUnit.split('-');
      const [targetBase, targetModifier] = targetUnit.split('-');

      const parseSourceBase = Qty.parse(sourceBase);
      if (!parseSourceBase) {
         throw new Error(JSON.stringify({ error: 'unknown_unit', unit: unitFrom, amount }));
      }

      const parseTargetBase = Qty.parse(targetBase);
      if (!parseTargetBase) {
         throw new Error(JSON.stringify({ error: 'unknown_unit', unit: targetUnit, amount }));
      }

      const parseSourceModifier = Qty.parse(sourceModifier);
      if (!parseSourceModifier) {
         throw new Error(JSON.stringify({ error: 'unknown_unit', unit: unitFrom, amount }));
      }

      const parseTargetModifier = Qty.parse(targetModifier);
      if (!parseTargetModifier) {
         throw new Error(JSON.stringify({ error: 'unknown_unit', unit: targetUnit, amount }));
      }

      const sourceBaseAmount = Qty.parse(`${amount}${sourceBase}`);

      if (!sourceBaseAmount) {
         throw new Error(JSON.stringify({ error: 'invalid_amount', amount }));
      }

      const sourceModifierAmount = Qty.parse(`${amount}${sourceModifier}`);

      if (!sourceModifierAmount) {
         throw new Error(JSON.stringify({ error: 'invalid_amount', amount }));
      }

      if (!parseSourceBase.isCompatible(parseTargetBase) || !parseSourceModifier.isCompatible(parseTargetModifier)) {
         throw new Error(JSON.stringify({ error: 'units_not_compatible', from: unitFrom ?? '', to: unitTo ?? '', amount }));
      }

      return sourceBaseAmount.to(targetBase).scalar * sourceModifierAmount.to(targetModifier).scalar;
   } else {
      const parseSource = Qty.parse(sourceUnit);

      if (!parseSource) {
         throw new Error(JSON.stringify({ error: 'unknown_unit', unit: unitFrom, amount }));
      }

      const parseTarget = Qty.parse(targetUnit);

      if (!parseTarget) {
         throw new Error(JSON.stringify({ error: 'unknown_unit', unit: targetUnit, amount }));
      }

      if (parseSource && parseTarget && !parseSource.isCompatible(parseTarget)) {
         throw new Error(JSON.stringify({ error: 'units_not_compatible', from: unitFrom ?? '', to: unitTo ?? '', amount }));
      }

      const sourceAmount = Qty.parse(`${amount}${sourceUnit}`);

      if (!sourceAmount) {
         throw new Error(JSON.stringify({ error: 'invalid_amount', amount }));
      }

      return sourceAmount.to(targetUnit).scalar;
   }
}

export const formatOptions = [
   'compactDisplay',
   'currencySign',
   'currencyDisplay',
   'localeMatcher',
   'maximumFractionDigits',
   'maximumSignificantDigits',
   'minimumFractionDigits',
   'minimumSignificantDigits',
   'minimumIntegerDigits',
   'notation',
   'signDisplay',
   'useGrouping',
   'unitDisplay',
   'roundingPriority',
] as const;

export function determineDefaultStyle(element: {
   unit?: string;
   currencyCode?: string;
   currencyId?: string;
   currency?: Currency | string | null;
   type?: KPIFieldTypeEnum | null;
}): Intl.NumberFormatOptions['style'] {
   if (element?.unit === '%') {
      return 'percent';
   }
   if (element?.unit) {
      return 'unit';
   }
   if (element?.currencyCode ?? element?.currencyId ?? (element?.currency as Currency)?.shortCode ?? element?.currency) {
      return 'currency';
   }
   if (element?.type === KPIFieldType.PERCENTAGE) {
      return 'percent';
   }
   return 'decimal';
}

export function getCurrencySymbol(intl: IntlShape, shortCode: string): string | undefined {
   const parts = intl.formatNumberToParts(0, {
      style: 'currency',
      currency: shortCode,
      currencyDisplay: 'narrowSymbol',
      signDisplay: 'always',
   });
   return parts.find((part) => part.type === 'currency')?.value;
}

export const KPIFieldTypePerUnit = Object.entries(UNITS).reduce(
   (acc, [kpiFieldType, units]) => {
      for (const unit of units) {
         acc[unit.value] = kpiFieldType as KPIFieldTypeEnum;
         acc[unit.label] = kpiFieldType as KPIFieldTypeEnum;
      }
      return acc;
   },
   Object.values(EmissionWeightUnits).reduce(
      (acc2, weightUnit) => ({
         ...acc2,
         [`${weightUnit} CO₂`]: KPIFieldType.EMISSIONS,
      }),
      {} as Record<string, KPIFieldTypeEnum>
   )
);
